import styled, { useTheme } from 'styled-components';
import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Caption,
  SectionHeader,
  SectionWrapper,
  Title,
  ScoreValueRow,
  ScoreValueLarge,
  ScoreMax,
  ConfidenceValueLarge,
} from './common';
import { SmallPortalTooltip } from '../../Tooltip';
import { TooltipContent, TooltipDetail } from '../Progress/Tooltip';
import Button from '../../elements/Button';
import { AssessmentTestSessionDetailResponse } from '../../../api/admin';
import { ResultSkillDetailModal } from './ResultSkillBreakdownModal';
import QuestionAnalysisModal from './StepQuestionsResults/QuestionAnalysisModal';
import { TestQuestionRow } from './StepQuestionsResults/StepQuestionsResultsTable';

// Helper: Calculate not tested objectives for a skill
const calculateNotTestedObjectives = (
  skillId: string,
  test: AssessmentTestSessionDetailResponse | null | undefined
): string[] => {
  if (!test?.steps) return [];

  // Get all objective IDs for this skill from test steps
  const allObjectivesForSkill = new Set<string>();
  test.steps.forEach((step) => {
    step.objectives?.forEach((objective) => {
      if (objective.id.startsWith(skillId + ':')) {
        allObjectivesForSkill.add(objective.id);
      }
    });
  });

  // Get tested objectives from results
  const skillScore = test.results?.skillScores.find((s) => s.skill === skillId);
  if (!skillScore) return Array.from(allObjectivesForSkill);

  const testedObjectives = new Set<string>();
  skillScore.objectivesByStatus.exceeded.forEach((o) =>
    testedObjectives.add(o.id)
  );
  skillScore.objectivesByStatus.met.forEach((o) => testedObjectives.add(o.id));
  skillScore.objectivesByStatus.partial.forEach((o) =>
    testedObjectives.add(o.id)
  );
  skillScore.objectivesByStatus.notMet.forEach((o) =>
    testedObjectives.add(o.id)
  );

  // Return objectives that weren't tested
  return Array.from(allObjectivesForSkill).filter(
    (objId) => !testedObjectives.has(objId)
  );
};

// Helper: Build mapping of objectives to scenarios/questions where they appear
// This uses the objective's facets.questions to find which steps contain those questions
const buildObjectiveScenarioMapping = (
  test: AssessmentTestSessionDetailResponse | null | undefined
): Map<
  string,
  Array<{ scenarioLabel: string; questionNumber: number; questionId: string }>
> => {
  const mapping = new Map<
    string,
    Array<{ scenarioLabel: string; questionNumber: number; questionId: string }>
  >();

  if (!test?.steps) return mapping;

  // For each step, collect which objectives appear based on their facets.questions
  test.steps.forEach((step) => {
    step.objectives?.forEach((objective) => {
      // Get all unique question IDs from the objective's facets
      const questionIds = [
        ...new Set(objective.facets.flatMap((facet) => facet.questions)),
      ];

      // For each question, find if it's in this step and at what position
      questionIds.forEach((questionId) => {
        const sectionIndex = step.sections.findIndex(
          (section) => section.id === questionId
        );

        if (sectionIndex !== -1) {
          if (!mapping.has(objective.id)) {
            mapping.set(objective.id, []);
          }

          const existing = mapping.get(objective.id);
          if (!existing) return;

          // Add the scenario label, question number (1-indexed), and question ID
          existing.push({
            scenarioLabel: step.label,
            questionNumber: sectionIndex + 1,
            questionId,
          });
        }
      });
    });
  });

  return mapping;
};

// Type for transformed skill data
export type TransformedSkill = {
  skill: string;
  skillName: string;
  score: number;
  confidence: number;
  comparativeLevel: string;
  iconUrl: string;
  objectivesByStatus: {
    exceeded: string[];
    met: string[];
    partial: string[];
    notMet: string[];
    notTested: string[];
  };
  keyStrengths: string;
  developmentAreas: string;
};

const ScrollContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SkillsContainer = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 16px;
  margin-bottom: -16px;

  /* Hide scrollbar for cleaner look */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.bgActive} transparent;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 0 24px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.bgActive};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.textPlaceholder}60;
  }
`;

const ScrollFade = styled.div<{ side: 'left' | 'right'; visible: boolean }>`
  position: absolute;
  top: 0;
  bottom: 16px;
  ${({ side }) => (side === 'left' ? 'left: 0;' : 'right: 0;')}
  width: 120px;
  pointer-events: none;
  z-index: 2;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.3s ease;

  background: ${({ side, theme }) =>
    side === 'left'
      ? `linear-gradient(to right, ${theme.colors.bgPage} 0%, transparent 100%)`
      : `linear-gradient(to left, ${theme.colors.bgPage} 0%, transparent 100%)`};

  @media (max-width: 768px) {
    width: 80px;
  }
`;

const SkillCard = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 28px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
  flex: 0 0 calc(33.333% - 16px);
  min-width: 320px;

  @media (max-width: 1024px) {
    flex: 0 0 calc(50% - 12px);
  }

  @media (max-width: 768px) {
    flex: 0 0 calc(100% - 24px);
  }
`;

const SkillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const SkillIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.bgActive};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 12px;
`;

const SkillIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  margin-top: 0;
  padding-bottom: 0;
`;

const SkillHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  /* gap: 4px; */
  flex: 1;
`;

const SkillName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.3px;
  line-height: 1.3;
`;

const SkillLevel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  text-transform: capitalize;
  opacity: 0.8;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const ScoreDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const ScoreLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textRegular};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 600;
  opacity: 0.7;
`;

const ScoreDivider = styled.div`
  width: 1px;
  height: 40px;
  background-color: ${({ theme }) => theme.colors.bgActive};
`;

const ConfidenceDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const ConfidenceLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textRegular};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 600;
  opacity: 0.7;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const InfoIcon = styled.i.attrs({
  className: 'fa-regular fa-circle-info',
})`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 10px;
  opacity: 0.6;
  cursor: help;

  &:hover {
    opacity: 1;
  }
`;

const ObjectiveSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ObjectiveSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textRegular};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 600;
  opacity: 0.8;
`;

const ObjectiveHeaderIcon = styled.i.attrs({
  className: 'fa-regular fa-chart-bar',
})`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  opacity: 0.7;
`;

const ObjectiveBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ViewDetailsButton = styled(Button)`
  justify-content: flex-start;

  margin-top: 0;
  border-radius: 8px;
  padding: 6px 16px;

  p {
    font-size: 12px;
  }
  i {
    font-size: 14px;
  }
`;

const BarTrack = styled.div`
  height: 10px;
  width: 100%;
  background: ${({ theme }) => theme.colors.bgActive};
  border-radius: 5px;
  overflow: hidden;
  display: flex;
`;

const BarSegment = styled.div<{ width: number; color: string }>`
  height: 100%;
  width: ${({ width }) => width}%;
  background-color: ${({ color }) => color};
  transition: width 0.4s ease;
`;

const BarLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textRegular};
  opacity: 0.8;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

interface ObjectiveStatusBarProps {
  objectivesByStatus: {
    exceeded: string[];
    met: string[];
    partial: string[];
    notMet: string[];
    notTested: string[];
  };
}

function ObjectiveStatusBar({ objectivesByStatus }: ObjectiveStatusBarProps) {
  const exceeded = objectivesByStatus.exceeded?.length || 0;
  const met = objectivesByStatus.met?.length || 0;
  const partial = objectivesByStatus.partial?.length || 0;
  const notMet = objectivesByStatus.notMet?.length || 0;
  const notTested = objectivesByStatus.notTested?.length || 0;

  const total = exceeded + met + partial + notMet + notTested;

  if (total === 0) return null;

  // Using shades of primary color (green) for tested objectives
  return (
    <ObjectiveBarContainer>
      <BarTrack>
        {exceeded > 0 && (
          <BarSegment width={(exceeded / total) * 100} color="#1DEA99" />
        )}
        {met > 0 && (
          <BarSegment width={(met / total) * 100} color="#1DEA9980" />
        )}
        {partial > 0 && (
          <BarSegment width={(partial / total) * 100} color="#1DEA9950" />
        )}
        {notMet > 0 && (
          <BarSegment width={(notMet / total) * 100} color="#1DEA9930" />
        )}
        {notTested > 0 && (
          <BarSegment width={(notTested / total) * 100} color="#FFFFFF15" />
        )}
      </BarTrack>
      <BarLegend>
        {exceeded > 0 && <LegendItem>{exceeded} exceeded</LegendItem>}
        {met > 0 && <LegendItem>{met} met</LegendItem>}
        {partial > 0 && <LegendItem>{partial} partial</LegendItem>}
        {notMet > 0 && <LegendItem>{notMet} not met</LegendItem>}
        {notTested > 0 && <LegendItem>{notTested} not tested</LegendItem>}
      </BarLegend>
    </ObjectiveBarContainer>
  );
}

export default function ResultsSkillsBreakdown({
  test,
}: {
  test: AssessmentTestSessionDetailResponse | null | undefined;
}) {
  const [selectedSkill, setSelectedSkill] =
    useState<TransformedSkill | null>(null);
  const [selectedQuestion, setSelectedQuestion] =
    useState<{
      questionId: string;
      scenarioLabel: string;
    } | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();

  const shouldShowResults = !!(test?.reviewedAt !== null && test?.results);

  // Transform skills data from API format to component format
  const skills = useMemo<TransformedSkill[]>(() => {
    if (!test?.dimensions) return [];

    // Use dimensions as the base, then merge in results when available
    return test.dimensions.map((dimension) => {
      const skillScore = test.results?.skillScores.find(
        (s) => s.skill === dimension.id
      );

      if (!skillScore) {
        // No results yet - return placeholder data
        return {
          skill: dimension.id,
          skillName: dimension.name,
          score: 0,
          confidence: 0,
          comparativeLevel: '',
          iconUrl: dimension.iconUrl || '',
          objectivesByStatus: {
            exceeded: [],
            met: [],
            partial: [],
            notMet: [],
            notTested: [],
          },
          keyStrengths: '',
          developmentAreas: '',
        };
      }

      // Results available - calculate full data
      const notTestedObjectives = calculateNotTestedObjectives(
        skillScore.skill,
        test
      );

      // Calculate confidence based on tested vs total objectives
      const testedCount =
        skillScore.objectivesByStatus.exceeded.length +
        skillScore.objectivesByStatus.met.length +
        skillScore.objectivesByStatus.partial.length +
        skillScore.objectivesByStatus.notMet.length;
      const totalCount = testedCount + notTestedObjectives.length;
      const confidence = totalCount > 0 ? testedCount / totalCount : 0;

      return {
        skill: dimension.id,
        skillName: dimension.name,
        score: skillScore.score,
        confidence,
        comparativeLevel: skillScore.comparativeLevel,
        iconUrl: dimension.iconUrl || '',
        objectivesByStatus: {
          exceeded: skillScore.objectivesByStatus.exceeded.map((o) => o.id),
          met: skillScore.objectivesByStatus.met.map((o) => o.id),
          partial: skillScore.objectivesByStatus.partial.map((o) => o.id),
          notMet: skillScore.objectivesByStatus.notMet.map((o) => o.id),
          notTested: notTestedObjectives,
        },
        keyStrengths: skillScore.keyStrengths,
        developmentAreas: skillScore.developmentAreas,
      };
    });
  }, [test]);

  // Build objective to scenario mapping
  const objectiveToScenarios = useMemo(() => {
    return buildObjectiveScenarioMapping(test);
  }, [test]);

  // Helper function to build question data for QuestionAnalysisModal
  const buildQuestionData = (
    questionId: string,
    scenarioLabel: string
  ): TestQuestionRow | null => {
    if (!test) return null;

    // Find the step that matches the scenario label
    const step = test.steps.find((s) => s.label === scenarioLabel);
    if (!step) return null;

    // Find the section (question) by ID
    const sectionIndex = step.sections.findIndex((s) => s.id === questionId);
    if (sectionIndex === -1) return null;
    const section = step.sections[sectionIndex];

    // Find the answer for this step
    const answer = test.answers.find((a) => a.id === step.id);
    const sectionAnswer = answer?.sections.find((s) => s.id === questionId);

    // Find the step evaluation
    const stepReview =
      test.results?.scenarioEvaluations.find((e) => e.scenarioId === step.id) ||
      null;

    // Calculate time spent
    const computeTimeFromIntervals = (
      intervals: Array<{ startedAt: Date; endedAt: Date | null }> | undefined
    ): number => {
      if (!intervals || intervals.length === 0) {
        return 0;
      }
      return intervals.reduce((acc, interval) => {
        const start = new Date(interval.startedAt).getTime();
        const end = interval.endedAt
          ? new Date(interval.endedAt).getTime()
          : new Date().getTime();
        return acc + (end - start) / 1000;
      }, 0);
    };

    const secondsSpent = sectionAnswer?.timeIntervals
      ? computeTimeFromIntervals(sectionAnswer.timeIntervals)
      : null;

    return {
      id: section.id,
      stepId: step.id,
      didStartTest: test.startedAt !== null,
      questionNumber: sectionIndex + 1,
      secondsSpent,
      section,
      answer: sectionAnswer || null,
      stepReview,
      stepObjectives: step.objectives || [],
      dimensions: test.dimensions || [],
    };
  };

  const questionData = selectedQuestion
    ? buildQuestionData(
        selectedQuestion.questionId,
        selectedQuestion.scenarioLabel
      )
    : null;

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  return (
    <>
      <SectionWrapper>
        <SectionHeader>
          <Title>Skills Breakdown</Title>
          <Caption>
            Detailed performance analysis across all assessed skill areas
          </Caption>
        </SectionHeader>
        <ScrollContainer>
          <ScrollFade side="left" visible={canScrollLeft} />
          <ScrollFade side="right" visible={canScrollRight} />
          <SkillsContainer ref={scrollContainerRef}>
            {skills.map((skill) => (
              <SkillCard key={skill.skill} isDisabled={!shouldShowResults}>
                <SkillHeader>
                  <SkillIconWrapper>
                    <SkillIcon src={skill.iconUrl} alt={skill.skillName} />
                  </SkillIconWrapper>
                  <SkillHeaderText>
                    <SkillName>{skill.skillName}</SkillName>
                    <SkillLevel>
                      {shouldShowResults ? skill.comparativeLevel : '-'}
                    </SkillLevel>
                  </SkillHeaderText>
                </SkillHeader>

                <ScoreRow>
                  <ScoreDisplay>
                    <ScoreLabel>Score</ScoreLabel>
                    <ScoreValueRow>
                      <ScoreValueLarge>
                        {shouldShowResults ? skill.score.toFixed(1) : '-'}
                      </ScoreValueLarge>
                      <ScoreMax>/ 10</ScoreMax>
                    </ScoreValueRow>
                  </ScoreDisplay>

                  <ScoreDivider />

                  <ConfidenceDisplay
                    data-tooltip-id={`confidence-${skill.skill}`}
                  >
                    <ConfidenceLabel>
                      Confidence <InfoIcon />
                    </ConfidenceLabel>
                    <ConfidenceValueLarge confidence={skill.confidence}>
                      {shouldShowResults
                        ? `${Math.round(skill.confidence * 100)}%`
                        : '-'}
                    </ConfidenceValueLarge>
                  </ConfidenceDisplay>
                </ScoreRow>

                <SmallPortalTooltip
                  anchorSelect={`[data-tooltip-id="confidence-${skill.skill}"]`}
                >
                  <TooltipContent>
                    <TooltipDetail>
                      Based on{' '}
                      {Math.round(
                        ((skill.objectivesByStatus.exceeded.length +
                          skill.objectivesByStatus.met.length +
                          skill.objectivesByStatus.partial.length +
                          skill.objectivesByStatus.notMet.length) /
                          (skill.objectivesByStatus.exceeded.length +
                            skill.objectivesByStatus.met.length +
                            skill.objectivesByStatus.partial.length +
                            skill.objectivesByStatus.notMet.length +
                            skill.objectivesByStatus.notTested.length)) *
                          100
                      )}
                      % of objectives tested
                    </TooltipDetail>
                  </TooltipContent>
                </SmallPortalTooltip>

                {shouldShowResults && (
                  <ObjectiveSection>
                    <ObjectiveSectionHeader>
                      <ObjectiveHeaderIcon />
                      Objectives Tested
                    </ObjectiveSectionHeader>
                    <ObjectiveStatusBar
                      objectivesByStatus={skill.objectivesByStatus}
                    />
                  </ObjectiveSection>
                )}

                {shouldShowResults && (
                  <div>
                    <ViewDetailsButton
                      type={'secondary'}
                      color={theme.colors.textActive}
                      label="View Breakdown"
                      faIcon="fa-regular fa-arrow-right"
                      onClick={() => setSelectedSkill(skill)}
                    />
                  </div>
                )}
              </SkillCard>
            ))}
          </SkillsContainer>
        </ScrollContainer>
      </SectionWrapper>

      {selectedSkill && test && (
        <ResultSkillDetailModal
          skill={selectedSkill}
          test={test}
          onClose={() => setSelectedSkill(null)}
          objectiveToScenarios={objectiveToScenarios}
          onQuestionClick={(questionId, scenarioLabel) => {
            setSelectedQuestion({ questionId, scenarioLabel });
            setSelectedSkill(null); // Close skill modal
          }}
        />
      )}

      {questionData &&
        selectedQuestion &&
        test &&
        (() => {
          const step = test.steps.find(
            (s) => s.label === selectedQuestion.scenarioLabel
          );
          if (!step) return null;

          const stepReview =
            test.results?.scenarioEvaluations.find(
              (e) => e.scenarioId === step.id
            ) || null;

          return (
            <QuestionAnalysisModal
              isOpen={true}
              onClose={() => setSelectedQuestion(null)}
              question={questionData}
              step={step}
              stepReview={stepReview}
            />
          );
        })()}
    </>
  );
}
